import { computed, defineComponent, h, ref, watch } from 'vue';
import { box, column, indexView, loader, pagination, tag, textColumn } from '@/ui/components';
import { filterFormLoader } from '@/ui/components/Loader';
import {
  conductedByFilter,
  lastUpdatedFilter,
  searchFilter,
  entityItemFilter,
} from '@/ui/components/filters/filters-horizontal';
import { bulkDelete, list as getInspectionList, remove } from '@/services/api/resources/inspection';
import { InspectionList, InspectionRecord } from '@/entities/inspection';
import { d, t } from '@/ui/i18n/i18n';
import { filterListToOptions, sequence } from '@/ui/helpers';
import { SelectOptions, SortDirection } from '@/ui/types';
import * as qs from '@/ui/queryState';
import { eventBus } from '@/_legacy/eventBus';
import { Events } from '@/ui/events';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
import { emptyList } from '@/services/api';
import { InspectionStatus, TodoRecord } from '@/entities/todo';
import { useInspectionCheck } from '@/ui/pages/inspection/useInspectionCheck';
import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
import { bulkDeleteButton } from '@/ui/components/Button';
import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';
import { manageIssueModal } from '@/ui/components/issue/ManageIssuesModal';
import { IssueType } from '@/entities/issue';
import { IssueRecord } from '@/entities/issue/v2';
import { user } from '@/ui/auth/auth';
import ContinueInspection from '@/ui/pages/forms/ContinueInspection.vue';
import InspectionDraftEmpty from '@/ui/pages/inspection/InspectionDraftEmpty.vue';

export const InspectionDraftTab = defineComponent({
  name: 'InspectionDraftTab',
  setup() {
    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir, onSortChange } = useSorting('lastUpdateAt', SortDirection.Descending);
    const { checkResult, performCheck } = useInspectionCheck([], [InspectionStatus.Draft]);

    const search = qs.string('q', '');
    const assignee = qs.numbers('assignee', []);
    const doneBy = qs.numbers('doneBy', []);
    const entityItem = qs.numbers('entityItem', []);
    const start = qs.nullableDate('start', null);
    const end = qs.nullableDate('end', null);
    const categories = qs.numbers('category', []);

    // get filters task
    const users = ref<SelectOptions>([]);
    const entityItems = ref<SelectOptions>([]);
    const getFilters = useTask(
      () => sequence(getAllUsers(), getAllEntityItems()),
      ([userList, entityItemsList]) => {
        users.value = filterListToOptions(userList);
        entityItems.value = filterListToOptions(entityItemsList);
      },
    );

    const activeInspectionManageIssues = ref<null | InspectionRecord>(null);

    // get list task
    const list = ref<InspectionList>(emptyList());
    const getList = useTask(
      () => getInspectionList({
        timezone: user.value!.timezone,
        page: page.value,
        perPage: perPage.value,
        search: search.value,
        assignee: assignee.value,
        entityItem: entityItem.value,
        status: [],
        inspectionStatus: [InspectionStatus.Draft],
        updatedAtStart: start.value,
        updatedAtEnd: end.value,
        categories: categories.value,
        sort: sort.value,
        dir: dir.value,
        overdue: null,
        questionTitle: [],
        response: [],
        doneBy: doneBy.value,
      }),
      listResponse => {
        list.value = listResponse;

        // if the modal to create an issue is opened
        // we need to get the inspection with the newest
        // issues and populate `activeInspectionManageIssues`.
        // This is used in `manageIssueModal` component.
        if (activeInspectionManageIssues.value) {
          activeInspectionManageIssues.value = (
            list.value.data.find(i => i.id === activeInspectionManageIssues.value!.id)!
          );
        }
      },
    );

    watch([
      page,
      perPage,
      search,
      assignee,
      entityItem,
      doneBy,
      start,
      sort,
      dir,
    ], getList.run);

    watch(() => getList.state.value.pending, newVal => {
      initialLoading.value = newVal;
    });

    const getLastUpdatedAt = (inspection: InspectionRecord) =>
      //can be null, 0, date. in case of 0 the year will be 1970
      inspection.data.updatedAt && inspection.data.updatedAt.getFullYear() > 1970
        ? d(inspection.data.updatedAt)
        : null;

    const {
      selectedResources,
      deleteBulkTask,
      onCheckedRowsUpdate,
    } = useBulkDeletable<InspectionRecord>({
      getList,
      bulkDeleteFn: bulkDelete,
      mapIds: record => record.data.inspection.id,
      onDeleteFilterFn: inspection => (
        currentElement => currentElement.data.inspection.id !== inspection.data.inspection.id
      ),
    });

    const isManageIssueModalOpen = ref(false);

    const closeModal = () => {
      isManageIssueModalOpen.value = false;
      activeInspectionManageIssues.value = null;
    };

    const isBulkDeletableButtonDisabled = computed(
      () => selectedResources.value.some(resource => !resource.authorizedTo.delete),
    );

    const mergeAttributesIssueCreation = computed(() => {
      if (activeInspectionManageIssues.value === null) {
        return {};
      }

      return {
        inspection: activeInspectionManageIssues.value.data.inspection.id,
        type: IssueType.ManualForm,
      };
    });

    const isOverdue = (inspection: InspectionRecord) => inspection.data.dueAt && inspection.data.dueAt < new Date();
    const onIssueCreated = () => getList.run();
    const onIssueUpdated = () => getList.run();

    const filterAutomaticFormAndManualFormIssues = (issue: IssueRecord) => (
      issue.data.type === IssueType.AutomaticForm || issue.data.type === IssueType.ManualForm
    );

    const issues = computed(() => activeInspectionManageIssues.value
      ? activeInspectionManageIssues
        .value
        .data
        .issues
        .filter(filterAutomaticFormAndManualFormIssues)
      : []);

    return function render() {
      return loader('div', performCheck, [
        isManageIssueModalOpen.value
          ? manageIssueModal({
            props: {
              active: isManageIssueModalOpen.value,
              mergeAttributes: mergeAttributesIssueCreation.value,
              issues: issues.value,
              issueListProps: {
                subtitle: t('inspections.issue-raised-submitted', {
                  numberIssuesRaised: issues.value.length.toString() ?? '0',
                }),
              },
            },
            on: {
              close: closeModal,
              'issue:created': onIssueCreated,
              'issue:updated': onIssueUpdated,
            },
          }) : null,

        checkResult.value ? indexView(
          [
            filterFormLoader(getFilters, [
              bulkDeleteButton({ resources: selectedResources, disabled: isBulkDeletableButtonDisabled }),
              searchFilter(search, resetPagination),
              lastUpdatedFilter(start, end, resetPagination),
              conductedByFilter(doneBy, users.value, resetPagination),
              entityItemFilter(entityItem, entityItems.value, resetPagination),
            ]),
          ],
          [
            box([
              loader('div', getList, [
                h(BulkDeletableTable, {
                  props: {
                    editRoute: 'inspectionEdit',
                    entityIdPath: ['data', 'inspection', 'id'],
                    list: list.value.data,
                    deleteFn: remove,
                    sort: sort.value,
                    dir: dir.value,
                    loading: deleteBulkTask.state.value.pending,
                    selectedRecords: selectedResources.value,
                    paginationLinks: list.value.meta,
                    isRowCheckable: () => true,
                    isHoverable: false,
                    recordName: {
                      plural: t('common.drafts'),
                      singular: t('common.draft'),
                    },
                  },
                  on: {
                    'update:checkedRows': onCheckedRowsUpdate,
                    sort: (field: string, direction: SortDirection) => qs.updateMultiple(() => {
                      onSortChange(field, direction);
                      resetPagination();
                    }),
                    deleted: (id: number) => eventBus.$emit(Events.inspectionDeleted, { id }),
                  },
                  scopedSlots: {
                    default: () => [
                      textColumn(
                        {
                          field: 'doneAt',
                          label: t('common.saved-at'),
                          sortable: true,
                        }, {
                          default: ({ row: inspection }: { row: InspectionRecord }) => [getLastUpdatedAt(inspection)],
                        },
                      ),
                      column(
                        {
                          field: 'title',
                          label: t('common.title'),
                          sortable: true,
                          cellClass: 'table__title-cell',
                        }, {
                          default: ({ row: inspection }: { row: InspectionRecord }) => [inspection.data.inspectionTitle || inspection.data.title],
                        },
                      ),
                      textColumn({
                        field: 'completedBy',
                        label: t('common.user'),
                        sortable: true,
                      }, {
                        default: ({ row: inspection }: { row: InspectionRecord }) =>
                          [inspection.data.doneBy ? inspection.data.doneBy.data.name : null],
                      }),
                      textColumn(
                        {
                          field: 'entityItem',
                          label: t('common.entity-item'),
                          sortable: true,
                        }, {
                          default: ({ row: inspection }: { row: InspectionRecord }) => inspection.data.entityItem
                            ? inspection.data.entityItem.data.title
                            : null,
                        },
                      ),
                      textColumn(
                        {
                          field: 'dueAt',
                          label: t('common.deadline'),
                          cellClass: 'deadline-column',
                          sortable: true,
                        }, {
                          default: ({ row: inspection }: { row: InspectionRecord }) => inspection.data.dueAt
                            ? [
                              h('span', d(inspection.data.dueAt)),
                              isOverdue(inspection) ? [tag('error', t('common.overdue'))] : '',
                            ]
                            : [tag('neutral', t('common.no-deadline'))],
                        },
                      ),
                    ],
                    beforeDeleteButton: ({ row: todo }: { row: TodoRecord }) => [
                      h(ContinueInspection, { props: { todo, tooltipContent: t('inspections.continue-form') } }),
                    ],
                  },
                }),
                pagination({
                  current: page.value,
                  total: list.value.meta.total,
                  perPage: perPage.value,
                }),
              ], !initialLoading.value),
            ]),
          ],
        ) : box([h(InspectionDraftEmpty)]),
      ], false);
    };
  },
});
